import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { formatMutationResource } from 'utils/misc';
import type { PreDrillWorkspace } from 'types/api-responses';
import type { PreDrillLandingInterpretation } from 'types/pre-drill-workspace';
import type { WellborePointsResponse } from 'types/wellbore-geometry-workspace';
import {
  markLandingCorrectAtom,
  removeLandingCorrectAtom,
  useLandingAnalysisRunAtom,
  useLandingAnalysisRunsAtom,
  useLandingInterpretationIntervalsAtom,
} from './atoms';
import {
  collectionOptions,
  currentIntervalId,
  intervalCards,
  catalogCard,
  isManualRun,
  landingAvailability,
  landingBanner,
  offeredRuns,
  runsInCollection,
  writeErrorMessage,
} from './utils';
import type { LandingBanner, LandingInterpretationIntervalRow } from './types';

interface UseLandingInterpretationParams {
  workspace: PreDrillWorkspace;
  wellWorkspaceId: number;
  points: WellborePointsResponse | null;
  pointsHaveDepths: boolean;
  /** True while the points read is in flight. Until it lands an empty point set says nothing
   *  about the bore, so availability is not resolved (no flash of the ready state). */
  pointsLoading?: boolean;
  /** Re-reads the bore show after a write, which carries the applied interpretation. */
  onChanged: () => void;
}

interface WriteFailure {
  kind: 'mark' | 'remove';
  message: string | null;
}

const WRITE_FAILURE_COPY: Record<
  WriteFailure['kind'],
  { title: string; fallback: string }
> = {
  mark: {
    title: 'Failed to mark the landing interpretation as correct.',
    fallback: 'Nothing was applied. Try again in a moment.',
  },
  remove: {
    title: 'Failed to remove correct status.',
    fallback: 'The interpretation is still applied. Try again in a moment.',
  },
};

// Review comment 5: a failed catalog read is said here too, not left as an empty basin.
const INTERVALS_FAILED_BANNER: LandingBanner = {
  kind: 'error',
  title: 'Failed to load the geologic intervals.',
  subtitle:
    'No interval can be picked from the catalog until they load. Open Select Geologic Interval to retry.',
};

// Review: a failed read of the selected run is said, not left as an empty deck under a banner
// asking for a run to be selected.
const RUN_DETAIL_FAILED_BANNER: LandingBanner = {
  kind: 'error',
  title: 'Failed to load the selected landing analysis.',
  subtitle:
    'Its intervals cannot be shown, so none can be marked correct from it. Retry, or choose another analysis.',
};

const RUNS_FAILED_BANNER: LandingBanner = {
  kind: 'error',
  title: 'Failed to load the landing analyses for this wellbore.',
  subtitle:
    'Without them a reading cannot be checked against its run, so no interval can be marked correct. Reload to try again.',
};

// What a write answered with, held until the bore show the host re-reads catches up (review
// item 8): without it the section briefly fell back to the pre-write state, re-enabling Mark as
// Correct over the system zone.
interface WriteEcho {
  applied: PreDrillLandingInterpretation | null;
}

// The Landing Analysis Interpretation section's state (INS-12936): the bore's runs and the run in
// view, the basin catalog with its nearby counts, the staged pick, and the two writes. Picking a
// card or a catalog row only STAGES a reading; Mark as Correct applies it to the bore's geometry
// workspace at once (the target-zone pattern), and the Well Workspace promote publishes it.
export const useLandingInterpretation = ({
  workspace,
  wellWorkspaceId,
  points,
  pointsHaveDepths,
  pointsLoading = false,
  onChanged,
}: UseLandingInterpretationParams) => {
  const { uwi14 } = workspace;
  const resources = workspace.resources;
  const writeHref = resources.landingInterpretation?.href ?? null;
  const readOnly = !resources.landingInterpretation?.auth.includes('create');
  const intervalsHref = resources.landingInterpretationIntervals?.href ?? null;

  const intervalsAtom = useLandingInterpretationIntervalsAtom(uwi14);
  const [runsResource, fetchRuns] = useAtom(useLandingAnalysisRunsAtom(uwi14));
  const [intervalsResource, fetchIntervals] = useAtom(intervalsAtom);
  const [markState, markCorrect] = useAtom(markLandingCorrectAtom);
  const [removeState, removeCorrect] = useAtom(removeLandingCorrectAtom);

  // Review item 7.1: the runs are re-read whenever the geometry or the bore's own state moves (a
  // Recalculate, a derive, a Mark or a Remove), not once per mount, so a run that lands while the
  // sheet is open is offered instead of the section reading interval-only until a reload. A
  // landing analysis finishing on its own sends this sheet no push; that still needs a reload.
  const [runsRefresh, setRunsRefresh] = useState(0);
  // Review comment 5: a failed catalog read is retried by bumping this, from the drawer's Retry
  // or on opening the picker after a failure.
  const [intervalsRefresh, setIntervalsRefresh] = useState(0);
  const refetchIntervals = useCallback(
    () => setIntervalsRefresh((count) => count + 1),
    []
  );
  const geometryKey = `${points?.geometryStatus ?? ''}|${
    points?.geometryStale ?? ''
  }|${workspace.geometryStatus}|${workspace.updatedAt}`;
  useEffect(() => {
    fetchRuns({
      type: 'POST',
      url: '/queries/well_landing_analyses',
      data: { uwi14s: [uwi14], status: 'processed', isPreview: false },
    });
  }, [uwi14, fetchRuns, geometryKey, runsRefresh]);

  // Fetched once with every interval, landed or not: the drawer filters client-side behind its
  // toggle, and the card deck reads any interval's count from the same set.
  useEffect(() => {
    if (!intervalsHref) return;
    fetchIntervals({
      type: 'POST',
      url: intervalsHref,
      data: {
        query: {
          well_workspace_id: wellWorkspaceId,
          uwi14,
          show_unlanded: true,
        },
      },
    });
  }, [intervalsHref, wellWorkspaceId, uwi14, fetchIntervals, intervalsRefresh]);

  const runsResult = formatMutationResource(runsResource);
  // Bot thread: a failed read is not "no analysis". Until the runs arrive, or when they fail,
  // nothing is offered, so a pick can never publish user_interval_only over a run that exists.
  const runsFailed =
    !!runsResource.result && runsResource.result.type !== 'success';
  const runsPending = !runsResult.data && !runsFailed;
  const runs = useMemo(
    () => offeredRuns(runsResult.data?.results ?? [], workspace.id),
    [runsResult.data, workspace.id]
  );
  const intervalsResult = formatMutationResource(intervalsResource);
  const intervalsFailed =
    !!intervalsResource.result && intervalsResource.result.type !== 'success';
  const intervals = useMemo(
    () => intervalsResult.data?.results ?? [],
    [intervalsResult.data]
  );

  const availability = landingAvailability({
    points,
    pointsHaveDepths,
    runCount: runs.length,
  });
  const ready = availability === 'ready';

  const [echo, setEcho] = useState<WriteEcho | null>(null);
  // The host's re-read has landed: the bore show is the truth again.
  useEffect(() => setEcho(null), [workspace]);
  const applied = echo
    ? echo.applied
    : (workspace.landingInterpretation ?? null);

  // The run the applied interpretation was read from, when there is one, opens selected.
  const appliedRun =
    applied && applied.sourceResultId !== null
      ? runs.find(
          (run) => run.wellLandingAnalysisResultId === applied.sourceResultId
        )
      : undefined;
  const collections = useMemo(() => collectionOptions(runs), [runs]);
  const [pickedCollectionId, setPickedCollectionId] = useState<number | null>(
    null
  );
  const collectionId =
    pickedCollectionId ??
    (appliedRun
      ? (appliedRun.structureGridCollection?.id ?? 0)
      : (collections[0]?.id ?? null));
  const collectionRuns = useMemo(
    () => runsInCollection(runs, collectionId),
    [runs, collectionId]
  );
  const [pickedRunId, setPickedRunId] = useState<number | null>(null);
  const run =
    collectionRuns.find((candidate) => candidate.id === pickedRunId) ??
    (appliedRun && collectionRuns.includes(appliedRun)
      ? appliedRun
      : collectionRuns[0]) ??
    null;

  const runAtom = useLandingAnalysisRunAtom(ready ? run?.href : null);
  const [runResource, refetchRun] = useAtom(runAtom);
  const runDetail =
    ready && runResource.data && !('error' in runResource.data)
      ? runResource.data
      : null;
  // Review: loading and failure are told apart from "no run", so a switch shows a spinner rather
  // than an empty deck, and a failed read says so.
  const runDetailFailed =
    ready && !!run && !!runResource.data && 'error' in runResource.data;
  const runDetailLoading = ready && !!run && !runDetail && !runDetailFailed;

  const [stagedId, setStagedId] = useState<number | null>(null);
  const currentId = ready ? currentIntervalId(runDetail) : null;
  // Applied beats staged beats the run's own zone, which starts selected in the ready state.
  const selectedId = applied?.geologicIntervalId ?? stagedId ?? currentId;
  const zoneChanged = ready && selectedId !== null && selectedId !== currentId;
  // Bot thread: a manual run's own zone is already this bore's landing; marking it correct again
  // would change nothing, so it is not offered.
  const confirmsManualRun = ready && !!run && isManualRun(run) && !zoneChanged;

  const cards = useMemo(() => {
    if (ready)
      return intervalCards({ run: runDetail, intervals, stagedId: selectedId });
    // With no analysis there is nothing to shortlist; the deck holds only the pick.
    const picked = intervals.find((interval) => interval.id === selectedId);
    if (picked) return [catalogCard(picked)];
    if (applied) {
      return [
        {
          id: applied.geologicIntervalId,
          name: applied.geologicInterval,
          percentInZone: null,
          nearbyLandedWells: null,
          gridSurface: null,
          isCurrent: false,
        },
      ];
    }
    return [];
  }, [ready, runDetail, intervals, selectedId, applied]);

  const [writeFailure, setWriteFailure] = useState<WriteFailure | null>(null);
  const selectedName =
    cards.find((card) => card.id === selectedId)?.name ??
    applied?.geologicInterval ??
    null;
  const failureBanner = (failure: WriteFailure): LandingBanner => ({
    kind: 'error',
    title: WRITE_FAILURE_COPY[failure.kind].title,
    subtitle: failure.message ?? WRITE_FAILURE_COPY[failure.kind].fallback,
  });
  let banner: LandingBanner;
  if (runsFailed) banner = RUNS_FAILED_BANNER;
  else if (runDetailFailed) banner = RUN_DETAIL_FAILED_BANNER;
  else if (intervalsFailed) banner = INTERVALS_FAILED_BANNER;
  else if (writeFailure) banner = failureBanner(writeFailure);
  else
    banner = landingBanner({
      availability,
      appliedName: applied ? selectedName : null,
      staged: stagedId !== null,
      zoneChanged,
    });

  // A new pick clears a failed write's banner: it described an attempt the operator moved past.
  const stage = (id: number | null) => {
    setWriteFailure(null);
    setStagedId(id);
  };

  const selectCollection = (id: number) => {
    setPickedCollectionId(id);
    setPickedRunId(null);
    stage(null);
  };
  const selectRun = (id: number) => {
    setPickedRunId(id);
    stage(null);
  };

  const markAsCorrect = useCallback(() => {
    if (!writeHref || selectedId === null) return;
    setWriteFailure(null);
    // A manual run is an interpretation, not a run to read from: its source is the system run it
    // was marked from (review), so a different zone marked on it saves user_derived, and its source's
    // own zone retires it. An interval-only manual run has none, and saves interval-only again.
    let sourceResultId: number | undefined;
    if (ready && run) {
      sourceResultId =
        (isManualRun(run)
          ? run.sourceResultId
          : run.wellLandingAnalysisResultId) ?? undefined;
    }
    markCorrect({
      type: 'POST',
      url: writeHref,
      data: {
        landing_interpretation: {
          geologic_interval_id: selectedId,
          ...(sourceResultId ? { source_result_id: sourceResultId } : {}),
        },
      },
      onSuccess: (show) => {
        setEcho({ applied: show?.landingInterpretation ?? null });
        setStagedId(null);
        setRunsRefresh((count) => count + 1);
        onChanged();
      },
      onError: (error) =>
        setWriteFailure({ kind: 'mark', message: writeErrorMessage(error) }),
    });
  }, [writeHref, selectedId, ready, run, markCorrect, onChanged]);

  const removeCorrectStatus = useCallback(() => {
    if (!writeHref) return;
    setWriteFailure(null);
    removeCorrect({
      type: 'DELETE',
      url: writeHref,
      data: {},
      onSuccess: (show) => {
        // The removed reading stays staged, so Mark as Correct re-applies it in one click, and the
        // collection and run stay as they were (review): with nothing applied they would fall back
        // to the defaults, and the next Mark would send a different source run.
        setStagedId(applied?.geologicIntervalId ?? null);
        setPickedCollectionId(collectionId);
        setPickedRunId(run?.id ?? null);
        setEcho({ applied: show?.landingInterpretation ?? null });
        setRunsRefresh((count) => count + 1);
        onChanged();
      },
      onError: (error) =>
        setWriteFailure({ kind: 'remove', message: writeErrorMessage(error) }),
    });
  }, [writeHref, applied, collectionId, run, removeCorrect, onChanged]);

  const stageInterval = (row: LandingInterpretationIntervalRow) =>
    stage(row.id);

  return {
    availability,
    readOnly,
    loading: runsPending || (points === null && pointsLoading),
    runsFailed,
    intervalsFailed,
    intervalsAtom,
    refetchIntervals,
    runDetailLoading,
    runDetailFailed,
    retryRunDetail: refetchRun,
    banner,
    collections,
    collectionId,
    selectCollection,
    collectionRuns,
    // A run read against a collection, so the catalog's Grid Surface column applies (UAT item 9).
    hasGridRun: runs.some((candidate) => !!candidate.structureGridCollection),
    run,
    selectRun,
    runDetail,
    cards,
    selectedId,
    canMark:
      !readOnly &&
      !runsFailed &&
      applied === null &&
      !runDetailLoading &&
      !runDetailFailed &&
      selectedId !== null &&
      !confirmsManualRun,
    stage,
    stageInterval,
    applied,
    busy: markState.loading || removeState.loading,
    markAsCorrect,
    removeCorrectStatus,
  };
};
